import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import { cn } from '@/utils/classname';

export interface AccordionItem {
  id: string;
  title: string;
  subtitle: string;
  content: React.ReactNode;
  defaultOpen?: boolean;
}

interface AccordionProps {
  items: AccordionItem[];
  className?: string;
  openItem?: string | null | string[];
  setOpenItem?: (id: string | null | string[]) => void;
  allowMultiple?: boolean;
  defaultOpenAll?: boolean;
}

export const Accordion = ({
  items,
  className,
  openItem: controlledOpenItem,
  setOpenItem: controlledSetOpenItem,
  allowMultiple = false,
  defaultOpenAll = false,
}: AccordionProps) => {
  const getInitialOpenItems = () => {
    if (allowMultiple) {
      if (defaultOpenAll) {
        return items.map(item => item.id);
      }
      return items.filter(item => item.defaultOpen).map(item => item.id);
    }
    return items.find(item => item.defaultOpen)?.id || null;
  };

  const [openItem, setOpenItem] = useState<string | null | string[]>(
    getInitialOpenItems()
  );

  const actualOpenItem =
    controlledOpenItem !== undefined ? controlledOpenItem : openItem;

  const toggleItem = (id: string) => {
    if (controlledSetOpenItem) {
      if (allowMultiple) {
        const currentArray = Array.isArray(actualOpenItem)
          ? actualOpenItem
          : [];
        if (currentArray.includes(id)) {
          controlledSetOpenItem(currentArray.filter(itemId => itemId !== id));
        } else {
          controlledSetOpenItem([...currentArray, id]);
        }
      } else {
        controlledSetOpenItem(actualOpenItem === id ? null : id);
      }
    } else {
      if (allowMultiple) {
        setOpenItem(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];
          if (prevArray.includes(id)) {
            return prevArray.filter(itemId => itemId !== id);
          }
          return [...prevArray, id];
        });
      } else {
        setOpenItem(prev => (prev === id ? null : id));
      }
    }
  };

  return (
    <div
      className={cn(
        'border-chariot-stroke flex flex-col gap-1 overflow-hidden rounded-lg border-2',
        className
      )}
    >
      {items.map(item => {
        const isOpen = allowMultiple
          ? Array.isArray(actualOpenItem) && actualOpenItem.includes(item.id)
          : actualOpenItem === item.id;
        return (
          <div key={item.id}>
            <button
              className="bg-chariot-background-secondary flex w-full items-center justify-between p-4 text-left"
              onClick={() => toggleItem(item.id)}
            >
              <div className="flex-1">
                <h3 className="text-chariot-text-primary text-lg font-bold">
                  {item.title}
                </h3>
                <p className="text-chariot-text-secondary mt-1 text-sm">
                  {item.subtitle}
                </p>
              </div>
              <div className="ml-4 shrink-0">
                <div className="bg-chariot-brand-primary flex size-6 items-center justify-center rounded-full">
                  {isOpen ? (
                    <MinusIcon className="text-chariot-text-primary size-4" />
                  ) : (
                    <PlusIcon className="text-chariot-text-primary size-4" />
                  )}
                </div>
              </div>
            </button>
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    duration: 0.3,
                    ease: [0.4, 0.0, 0.2, 1],
                  }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4">
                    <div className="pt-4">{item.content}</div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};
